import { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHomeSiteObserver } from "../../../reactQuery/homeSites/homsites/homeSiteQuery";
import { useExceptionsData } from "../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { useNotesData } from "../../../reactQuery/homeSites/notes/homeSiteNotes";
import { useAlertsData } from "../../../reactQuery/homeSites/alerts/homesiteAlerts";
import { getAuth } from "../../../redux/auth/authSlice";
import {
  TableColumnMap,
  useHomesite,
} from "../../../components/core/homesites/homesiteColumns";
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridPinnedColumnFields,
} from "@mui/x-data-grid-premium";
import {
  defaultNonVisibleColumnsArray,
  defaultTabOption,
  optionsTabs,
  syncRevisedEcoeDelayedReasonVisibility,
} from "./buyerListingUtils";
import { MetricKey } from "./buyerListingTypes";
import {
  ALERT_ACTIVE_STATUS,
  DEFAULT_VIEW_NAME,
  paramBuyerListingPeriod,
  paramBuyerListingTab,
  paramsBuyerListingValues,
} from "../../../utils/constants";
import { useSearchParams } from "react-router-dom";
import { closingsMyConciergeOverviewBuyers } from "../../../utils/fields";
import {
  IColumnVisibilityModel,
  PREF_BUYERS,
} from "../../../api/concierges/preferences";
import { usePreferences } from "../../../reactQuery/preferences/preferences";
import { Note } from "../../../api/homeSiteNotes/types";
import { Exception } from "../../../api/homeSiteExceptions/types";
import { Alert } from "../../../api/homeSiteAlerts/types";
import { useDivisionMileStoneDataReleased } from "../../../reactQuery/division/divisionMileStoneQuery";
import { MILESTONE_FILTER_PARAMS, MILESTONE_FILTER_VALUES } from "../../../components/milestoneFilterDropdown/types";
import { DivisionMileStone } from "../../../api/divisionMileStones/types";
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
import { useHomeSiteDataIndexQuery } from "../../../reactQuery/homeSitesDataIndex/homeSiteDataIndexQuery";
import { useMileStoneTasksData, useAllMilestoneTasksData } from "../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";
import { calculateMilestoneProgress } from "./milestoneProgressUtils";

const initializeColumnVisibilityModel = (
  columns: GridColDef[]
): { [key: string]: boolean } => {
  const visibilityModel: { [key: string]: boolean } = {};
  columns.forEach((column: GridColDef) => {
    visibilityModel[column.field] = true;
  });
  return visibilityModel;
};
export const useBuyerListingData = () => {
  const { userInfo } = useSelector(getAuth);
  const { setBuyerlistingTables } = useHomesite();
  const [getParams, setGetParams] = useSearchParams();
  const getPeriod = getParams.get(paramBuyerListingPeriod);
  const getTab = getParams.get(paramBuyerListingTab);
  const getSelectedMilestone = getParams.get(MILESTONE_FILTER_PARAMS.milestone);
  const buyerNameField = closingsMyConciergeOverviewBuyers.buyerName.field;
  const { data: homeSitesDataIndex, pending: isLoadingHomeSites } = useHomeSiteDataIndexQuery(userInfo.userDivCode);
  /* Initialize buyer Listing preference with "Default View" */
  const [selectedViewFromBuyerListing, setSelectedViewFromBuyerListing] = useState<string>(DEFAULT_VIEW_NAME);
  const [orderedColumns, setOrderedColumns] = useState<string[]>([]);
  const [columnReordered, setColumnReordered] = useState(false);
  const [enrichedRows, setEnrichedRows] = useState<HomeSitesDataIndex[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>(getPeriod ? getPeriod : paramsBuyerListingValues.allBuyers);
  const getOptionTabByFilter = optionsTabs.filter((item: any) => item.title === getTab)[0];
  const [selectedTab, setSelectedTab] = useState(getOptionTabByFilter || defaultTabOption);


  const { metrics: counts, applyDynamicFilters } = useHomeSiteObserver(homeSitesDataIndex || []);
  const { data: notes } = useNotesData(userInfo.userDivCode);
  const { data: exceptions } = useExceptionsData(userInfo.userDivCode);
  const { data: alerts } = useAlertsData(userInfo.userDivCode);
  const { data: preferences, isLoading: isLoadingPreference } = usePreferences(userInfo?.email, PREF_BUYERS, userInfo.userDivCode);
  const { data: divisionMilestones } = useDivisionMileStoneDataReleased(userInfo.userDivCode);
  const { data: milestoneTasks } = useMileStoneTasksData(userInfo.userDivCode);
  
  // Fetch all tasks with pagination for accurate progress calculation
  const { data: allMilestoneTasks } = useAllMilestoneTasksData(userInfo.userDivCode);

  const filteredHomesites = useMemo(() => {
    const selectedHomeSiteIds: string[] = counts[selectedMetric as MetricKey]?.homeSiteIds;
    if (!selectedHomeSiteIds || selectedHomeSiteIds.length === 0) return [];
    // Use Set for O(1) lookup instead of O(n) includes()
    const selectedHomeSiteIdsSet = new Set(selectedHomeSiteIds);
    return homeSitesDataIndex?.filter((hs) => selectedHomeSiteIdsSet.has(hs.homeSiteId)) || [];
  }, [selectedMetric, counts, homeSitesDataIndex]);

  const ecoeMismatchHomeSiteIdsSet = useMemo(
    () => new Set(counts.ecoeMismatch.homeSiteIds),
    [counts.ecoeMismatch.homeSiteIds]
  );

  // Build lookup maps for quick access by homeSiteId
  const notesByHomeSite = useMemo(() => {
    if (!notes?.data) return {};
    return notes.data.reduce((acc: Record<string, Note[]>, note: Note) => {
      if (note.status && note.noteType === "homeSiteNotes") {
        (acc[note.homeSiteId] = acc[note.homeSiteId] || []).push(note);
      }
      return acc;
    }, {});
  }, [notes?.data]);

  const exceptionsByHomeSite = useMemo(() => {
    if (!exceptions?.data) return {};
    return exceptions.data.reduce((acc: Record<string, Exception[]>, ex: Exception) => {
      if (ex.status) {
        (acc[ex.homeSiteId] = acc[ex.homeSiteId] || []).push(ex);
      }
      return acc;
    }, {});
  }, [exceptions?.data]);

  const alertsByHomeSite = useMemo(() => {
    if (!alerts?.data) return {};
    return alerts.data.reduce((acc: Record<string, Alert[]>, alert: Alert) => {
      if (alert.status === ALERT_ACTIVE_STATUS) {
        (acc[alert.homeSiteId] = acc[alert.homeSiteId] || []).push(alert);
      }
      return acc;
    }, {});
  }, [alerts?.data]);

  // Calculate milestone progress for each homesite
  const milestoneProgressByHomeSite = useMemo(() => {
    return calculateMilestoneProgress(
      allMilestoneTasks?.data,
      divisionMilestones?.data,
      getSelectedMilestone
    );
  }, [allMilestoneTasks?.data, divisionMilestones?.data, getSelectedMilestone]);

  useEffect(() => {
    if (!filteredHomesites || filteredHomesites.length === 0) {
      setEnrichedRows([]);
      return;
    }

    const hasMilestoneFilter = getSelectedMilestone && getSelectedMilestone !== MILESTONE_FILTER_VALUES.allMilestones;
    
    // Pre-compute milestone-filtered homeSiteIds if milestone filter is active
    let milestoneFilteredHomeSiteIds: Set<string> | null = null;
    if (hasMilestoneFilter) {
      milestoneFilteredHomeSiteIds = new Set<string>();
      
      // Process exceptions
      if (exceptions?.data) {
        for (const ex of exceptions.data) {
          if (ex.status && ex.mileStoneName === getSelectedMilestone) {
            milestoneFilteredHomeSiteIds.add(ex.homeSiteId);
          }
        }
      }
      
      // Process notes
      if (notes?.data) {
        for (const note of notes.data) {
          if (note.status && note.noteType === "homeSiteNotes" && note.mileStoneName === getSelectedMilestone) {
            milestoneFilteredHomeSiteIds.add(note.homeSiteId);
          }
        }
      }
      
      // Process alerts
      if (alerts?.data) {
        for (const alert of alerts.data) {
          if (alert.status === ALERT_ACTIVE_STATUS && alert.mileStoneName === getSelectedMilestone) {
            milestoneFilteredHomeSiteIds.add(alert.homeSiteId);
          }
        }
      }
    }

    // Build rows efficiently
    const rows: HomeSitesDataIndex[] = [];
    for (const row of filteredHomesites) {
      // Skip if milestone filter is active and this homeSite doesn't have data for that milestone
      if (hasMilestoneFilter && milestoneFilteredHomeSiteIds && !milestoneFilteredHomeSiteIds.has(row.homeSiteId)) {
        continue;
      }

      let filteredNotes = notesByHomeSite[row.homeSiteId] || [];
      let filteredExceptions = exceptionsByHomeSite[row.homeSiteId] || [];
      let filteredAlerts = alertsByHomeSite[row.homeSiteId] || [];

      // Filter by milestone if needed
      if (hasMilestoneFilter) {
        filteredNotes = filteredNotes.filter(n => n.mileStoneName === getSelectedMilestone);
        filteredExceptions = filteredExceptions.filter(e => e.mileStoneName === getSelectedMilestone);
        filteredAlerts = filteredAlerts.filter(a => a.mileStoneName === getSelectedMilestone);
      }

      const milestoneProgressData = milestoneProgressByHomeSite[row.homeSiteId] || { 
        progress: 0, 
        completed: 0, 
        total: 0,
        milestones: []
      };
      rows.push({
        ...row,
        numExceptions: filteredExceptions.length,
        numNotes: filteredNotes.length,
        numAlerts: filteredAlerts.length,
        ecoeMismatch: ecoeMismatchHomeSiteIdsSet.has(row.homeSiteId),
        milestoneProgress: milestoneProgressData.progress,
        milestoneProgressCompleted: milestoneProgressData.completed,
        milestoneProgressTotal: milestoneProgressData.total,
        milestoneProgressBreakdown: milestoneProgressData.milestones,
        notesData: filteredNotes,
      });
    }

    setEnrichedRows(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notes?.data,
    exceptions?.data,
    alerts?.data,
    filteredHomesites,
    getSelectedMilestone,
    notesByHomeSite,
    exceptionsByHomeSite,
    alertsByHomeSite,
    ecoeMismatchHomeSiteIdsSet,
    milestoneProgressByHomeSite,
  ]);

  // Filter counts based on selected milestone
  const filteredCounts = useMemo(() => {
    if (!getSelectedMilestone || getSelectedMilestone === MILESTONE_FILTER_VALUES.allMilestones) {
      return counts; // Return original counts if no milestone filtering
    }

    // Helper to extract homeSiteIds from a data array by milestone
    const getIdsByMilestone = (dataArr: any[] | undefined) => {
      if (!dataArr) return [];
      return dataArr
        .filter(item => item.mileStoneName === getSelectedMilestone)
        .map(item => item.homeSiteId);
    };

    // Collect all homeSiteIds from exceptions, notes, and alerts
    const exceptionIds = getIdsByMilestone(exceptions?.data);
    const notesIds = getIdsByMilestone(notes?.data);
    const alertsIds = getIdsByMilestone(alerts?.data);

    // Combine and dedupe
    const filteredHomeSiteIds = Array.from(new Set([
      ...exceptionIds,
      ...notesIds,
      ...alertsIds,
    ]));

    const filtered = { ...counts };

    // Use Set for O(1) lookup instead of O(n) includes()
    const filteredHomeSiteIdsSet = new Set(filteredHomeSiteIds);
    
    // Filter each metric's homeSiteIds
    Object.keys(filtered).forEach(metricKey => {
      const metric = (filtered as any)[metricKey];
      if (metric?.homeSiteIds) {
        const filteredIds = metric.homeSiteIds.filter((id: string) =>
          filteredHomeSiteIdsSet.has(id)
        );
        (filtered as any)[metricKey] = {
          ...metric,
          homeSiteIds: filteredIds,
          count: filteredIds.length
        };
      }
    });

    return filtered;
  }, [counts, getSelectedMilestone, exceptions?.data, notes?.data, alerts?.data]);

  const createColumns = useCallback(
    (fields: TableColumnMap) => setBuyerlistingTables(fields),
    []
  );
  const hsColumns = useMemo<GridColDef[]>(
    () => createColumns(TableColumnMap.BuyerListingFields) as GridColDef[],
    [createColumns]
  );
  const buyerColumns = useMemo<GridColDef[]>(
    () => createColumns(TableColumnMap.BuyerListingBuyerFields) as GridColDef[],
    [createColumns]
  );
  const homesiteColumns = useMemo<GridColDef[]>(
    () =>
      createColumns(TableColumnMap.BuyerListingHomeSiteFields) as GridColDef[],
    [createColumns]
  );
  const mortgageTitleColumns = useMemo<GridColDef[]>(
    () =>
      createColumns(TableColumnMap.BuyerListingMortgageTitleFields) as GridColDef[],
    [createColumns]
  );

  const oslColumns = useMemo<GridColDef[]>(
    () =>
      createColumns(TableColumnMap.BuyerListingOSLFields) as GridColDef[],
    [createColumns]
  );

  const defaultNonVisibleColumns = useMemo(
    () => defaultNonVisibleColumnsArray,
    []
  );
  const [curColumns, setCurColumns] = useState<GridColDef[]>(hsColumns);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<{
    [key: string]: boolean;
  }>(() => initializeColumnVisibilityModel(hsColumns));
  const [columnVisibilityModelTabs] = useState<{ [key: string]: boolean }>(() =>
    initializeColumnVisibilityModel(hsColumns)
  );
  const handleColumnVisibilityChange = (newModel: IColumnVisibilityModel) => {
    setColumnVisibilityModel((previousModel) =>
      syncRevisedEcoeDelayedReasonVisibility(newModel, previousModel)
    );
  };

  /**
   * Reorder unpinned columns while keeping pinned (left/right) columns fixed.
   * Visual indices include pinned columns; translate them into unpinned segment indices.
   */
  const handleColumnOrderChange = (
    params: GridColumnOrderChangeParams,
    pinned: GridPinnedColumnFields
  ) => {
    const { oldIndex, targetIndex } = params;
    const left = pinned.left ?? [];
    const right = pinned.right ?? [];

    const allFieldsBase = hsColumns.map(c => c.field);

    let currentFullOrder: string[];
    if (
      orderedColumns.length === 0 ||
      orderedColumns.length !== allFieldsBase.length ||
      !allFieldsBase.every(f => orderedColumns.includes(f))
    ) {
      const leftSet = new Set(left);
      const rightSet = new Set(right);
      const unpinned = allFieldsBase.filter(
        f => !leftSet.has(f) && !rightSet.has(f)
      );
      currentFullOrder = [...left, ...unpinned, ...right];
    } else {
      currentFullOrder = [...orderedColumns];
    }

    const leftCount = left.length;
    const rightCount = right.length;
    const unpinnedCount = currentFullOrder.length - leftCount - rightCount;
    const lastUnpinnedIndex = leftCount + unpinnedCount - 1;
    const dragInUnpinned = oldIndex >= leftCount && oldIndex <= lastUnpinnedIndex;
    const dropInUnpinned = targetIndex >= leftCount && targetIndex <= lastUnpinnedIndex;
    if (!dragInUnpinned || !dropInUnpinned) return;

    const unpinnedSlice = currentFullOrder.slice(leftCount, leftCount + unpinnedCount);
    const from = oldIndex - leftCount;
    const to = targetIndex - leftCount;
    const moving = unpinnedSlice.splice(from, 1)[0];
    unpinnedSlice.splice(to, 0, moving);

  const newFull = [...left, ...unpinnedSlice, ...right];
  setOrderedColumns(newFull);
    setColumnReordered(true);
  };

  useEffect(() => {
    switch (selectedTab.value) {
      case 0:
        const reOrderedCols =
          orderedColumns.length > 0
            ? orderedColumns
              .map((field) => hsColumns.find((col) => col.field === field))
              .filter((col): col is GridColDef => col !== undefined)
            : hsColumns;
        setCurColumns(reOrderedCols);
        break;
      case 1:
        setCurColumns(buyerColumns);
        break;
      case 2:
        setCurColumns(homesiteColumns);
        break;
      case 3:
        setCurColumns(mortgageTitleColumns);
        break;
      case 4:
        setCurColumns(oslColumns);
        break;
      default:
        setCurColumns(hsColumns);
        break;
    }
  }, [
    selectedTab,
    hsColumns,
    homesiteColumns,
    mortgageTitleColumns,
    oslColumns,
    buyerColumns,
    orderedColumns,
  ]);

  // Memoize filteredHomeSiteIds separately for better performance
  const filteredHomeSiteIds = useMemo(() => {
    if (!filteredHomesites) return new Set<string>();
    return new Set(filteredHomesites.map(hs => hs.homeSiteId));
  }, [filteredHomesites]);

  // Pre-initialize milestone counts structure for better performance
  const initializedMilestoneCounts = useMemo(() => {
    if (!divisionMilestones) return {};

    const counts: { [key: string]: { exceptionCount: number, alertsCount: number, notesCount: number, generalCommentsCount: number } } = {};

    // Pre-initialize all known milestones
    divisionMilestones.data.forEach((milestone: DivisionMileStone) => {
      counts[milestone.mileStoneName] = {
        exceptionCount: 0,
        alertsCount: 0,
        notesCount: 0,
        generalCommentsCount: 0
      };
    });

    return counts;
  }, [divisionMilestones]);

  // Calculate milestone-specific counts for dropdown (only for currently filtered homeSites)
  const milestoneSpecificCounts = useMemo(() => {
    // Early return for performance
    if (!divisionMilestones ||
      (!exceptions?.data && !notes?.data && !alerts?.data) ||
      !filteredHomesites ||
      filteredHomeSiteIds.size === 0) {
      return {};
    }

    // Clone the pre-initialized counts for efficiency (avoid expensive JSON operations)
    const counts: { [key: string]: { exceptionCount: number, alertsCount: number, notesCount: number, generalCommentsCount: number } } = {};
    for (const [key, value] of Object.entries(initializedMilestoneCounts)) {
      counts[key] = { ...value };
    }

    // Helper function to ensure milestone exists in counts
    const ensureMilestone = (milestoneName: string) => {
      if (!counts[milestoneName]) {
        counts[milestoneName] = {
          exceptionCount: 0,
          alertsCount: 0,
          notesCount: 0,
          generalCommentsCount: 0
        };
      }
    };

    // Process alerts efficiently
    if (alerts?.data && alerts.data.length > 0) {
      for (const alert of alerts.data) {
        if (alert.status === ALERT_ACTIVE_STATUS &&
          alert.mileStoneName &&
          filteredHomeSiteIds.has(alert.homeSiteId)) {
          ensureMilestone(alert.mileStoneName);
          counts[alert.mileStoneName].alertsCount++;
        }
      }
    }

    // Process exceptions efficiently
    if (exceptions?.data && exceptions.data.length > 0) {
      for (const exception of exceptions.data) {
        if (exception.status &&
          exception.mileStoneName &&
          filteredHomeSiteIds.has(exception.homeSiteId)) {
          ensureMilestone(exception.mileStoneName);
          counts[exception.mileStoneName].exceptionCount++;
        }
      }
    }

    // Process notes efficiently
    if (notes?.data && notes.data.length > 0) {
      for (const note of notes.data) {
        if (note.status &&
          note.noteType === "homeSiteNotes" &&
          note.mileStoneName &&
          filteredHomeSiteIds.has(note.homeSiteId)) {

          ensureMilestone(note.mileStoneName);

          // Check if this is a general comment
          if (note.mileStoneName === "General Comments") {
            counts[note.mileStoneName].generalCommentsCount++;
          } else {
            counts[note.mileStoneName].notesCount++;
          }
        }
      }
    }

    return counts;
  }, [initializedMilestoneCounts, exceptions?.data, notes?.data, alerts?.data, filteredHomeSiteIds, divisionMilestones, filteredHomesites]);

  return {
    userInfo,
    homeSitesDataIndex,
    isLoadingHomeSites,
    isLoadingPreference,
    preferences,
    counts: filteredCounts,
    applyDynamicFilters,
    enrichedRows,
    hsColumns,
    defaultNonVisibleColumns,
    setSelectedMetric,
    getParams,
    setGetParams,
    optionsTabs,
    buyerNameField,
    selectedTab,
    setSelectedTab,
    setColumnVisibilityModel,
    columnVisibilityModel,
    curColumns,
    columnVisibilityModelTabs,
    handleColumnVisibilityChange,
    handleColumnOrderChange,
    orderedColumns,
    columnReordered,
    setOrderedColumns,
    divisionMilestones,
    milestoneSpecificCounts,
    selectedMilestone: getSelectedMilestone,
    selectedViewFromBuyerListing,
    setSelectedViewFromBuyerListing,
  };
};

